import React, { useState } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useCurrentUser } from "../context/AuthContext";
import ProfileForm, { ProfileFields } from "../components/ProfileForm";

const CREATE_PROFILE = gql`
  mutation CreateUserProfile($userId: Int!, $fullName: String, $displayName: String, $hometown: String, $school: String, $work: String, $interests: String, $bio: String, $preferredSaunaTempF: Int) {
    createUserProfile(input: {
      userProfile: {
        userId: $userId
        fullName: $fullName
        displayName: $displayName
        hometown: $hometown
        school: $school
        work: $work
        interests: $interests
        bio: $bio
        preferredSaunaTempF: $preferredSaunaTempF
      }
    }) {
      userProfile {
        id
      }
    }
  }
`;

const CREATE_ACKNOWLEDGED_TERMS = gql`
  mutation CreateAcknowledgedTerms($userId: Int!) {
    createAcknowledgedTerm(input: {
      acknowledgedTerm: { userId: $userId, isHost: true, isGuest: true }
    }) {
      acknowledgedTerm { id }
    }
  }
`;

const UPDATE_SMS_PREF = gql`
  mutation ProfileSetupSetSmsPref($userId: Int!, $enabled: Boolean!) {
    updateUserById(input: { id: $userId, userPatch: { smsNotificationsEnabled: $enabled } }) {
      user { id }
    }
  }
`;

const GET_USER_PHONE = gql`
  query GetUserPhone($userId: Int!) {
    userById(id: $userId) {
      phone
    }
  }
`;

export default function ProfileSetup() {
  const userId = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const [createProfile, { loading }] = useMutation(CREATE_PROFILE);
  const [createAck, { loading: ackLoading }] = useMutation(CREATE_ACKNOWLEDGED_TERMS);
  const [updateSmsPref] = useMutation(UPDATE_SMS_PREF);
  const { data: userData } = useQuery(GET_USER_PHONE, {
    variables: { userId },
    skip: !userId,
  });
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [fields, setFields] = useState<ProfileFields>({
    fullName: "", displayName: "", hometown: "", school: "", work: "", interests: "", bio: "", preferredSaunaTempF: "",
  });
  const hasPhone = !!userData?.userById?.phone;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const { errors } = await createProfile({
        variables: {
          userId,
          ...fields,
          preferredSaunaTempF: fields.preferredSaunaTempF ? parseInt(fields.preferredSaunaTempF, 10) : null,
        },
      });
      if (errors) { setError(errors[0]?.message ?? "Failed to save profile"); return; }
      await Promise.all([
        createAck({ variables: { userId } }),
        updateSmsPref({ variables: { userId, enabled: smsOptIn } }),
      ]);
      navigate(next || "/listings");
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong");
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: "60px auto", padding: 24 }}>
      <h2>Tell hosts a bit about yourself</h2>
      <p style={{ color: "#6b7280", marginBottom: 24 }}>
        Hosts use this to get to know who's booking their sauna.
      </p>
      {error && <p style={{ color: "#ef4444", marginBottom: 16 }}>{error}</p>}
      <form onSubmit={handleSubmit}>
        <ProfileForm fields={fields} onChange={setFields} />
        <div style={termsBox}>
          <p style={{ margin: "0 0 10px", fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
            Saunas involve heat, water, and inherent physical risk. By continuing you acknowledge that
            accidents, serious injury, or even death may occur. This application accepts complete and
            utter zero responsibility for any harm, illness, or property damage arising from sauna
            use, whether as a host or a guest.
          </p>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
            <span>I acknowledge and accept these terms.</span>
          </label>
        </div>
        {hasPhone && (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "16px 0 4px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={smsOptIn}
              onChange={e => setSmsOptIn(e.target.checked)}
              style={{ marginTop: 3, flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
              Send me SMS booking notifications (requests, confirmations, cancellations). Optional. Message and data rates may apply. Reply <strong>STOP</strong> to opt out.{" "}
              <Link to="/terms" style={{ color: "#2563eb" }}>Terms</Link>
              {" | "}
              <Link to="/privacy" style={{ color: "#2563eb" }}>Privacy</Link>
            </span>
          </label>
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button type="submit" disabled={loading || ackLoading || !agreed} style={primaryBtn}>
            {loading || ackLoading ? "Saving..." : "Save and continue"}
          </button>
          <button type="button" onClick={() => navigate(next || "/listings")} style={skipBtn}>
            Skip for now
          </button>
        </div>
      </form>
    </div>
  );
}

const termsBox: React.CSSProperties = {
  marginTop: 24, padding: "14px 16px", background: "#f9fafb",
  border: "1px solid #e5e7eb", borderRadius: 6,
};
const primaryBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#2563EB", color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
const skipBtn: React.CSSProperties = {
  padding: "10px 24px", background: "transparent", color: "#6b7280",
  border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
